import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import {
  Edit,
  Category,
  Schedule,
  Visibility,
  Public,
  LocationOn,
  Warning,
} from "@mui/icons-material";

// Helper function to extract unique locations from a menu item
const getItemLocations = (menuItem) => {
  const locations = new Set();
  
  if (menuItem.variantEnabled && menuItem.variants && menuItem.variants.length > 0) {
    // If variants enabled, get locations from perLocationStock in each variant
    menuItem.variants.forEach(variant => {
      if (variant.perLocationStock && Array.isArray(variant.perLocationStock)) {
        variant.perLocationStock.forEach(locationStock => {
          if (locationStock.businessLocationName) {
            locations.add(locationStock.businessLocationName);
          }
        });
      }
    });
  } else if (menuItem.locationStock && Array.isArray(menuItem.locationStock)) {
    // If variants disabled, get locations from locationStock
    menuItem.locationStock.forEach(locationStock => {
      if (locationStock.businessLocationName) {
        locations.add(locationStock.businessLocationName);
      }
    });
  }
  
  return Array.from(locations);
};

// Edit Menu/Catalog Dialog Component
const EditMenuDialog = ({
  open,
  onClose,
  editingMenu,
  editFormData,
  setEditFormData,
  locationList,
  tabValue,
  onSave,
  onScheduleClick,
  onItemsListClick,
  onItemClick,
  isLoading,
  dialogTitle,
}) => {
  const { t } = useTranslation("dashboard");
  // Ensure showStockCount is always true
  React.useEffect(() => {
    if (editFormData.showStockCount !== true) {
      setEditFormData(prev => ({...prev, showStockCount: true}));
    }
  }, [editFormData.showStockCount, setEditFormData]);

  // Get selected menu location names
  const selectedMenuLocationNames = React.useMemo(() => {
    if (!editFormData?.selectedLocations || !locationList) return [];
    return locationList
      .filter(loc => editFormData.selectedLocations.includes(loc._id))
      .map(loc => loc.businessLocationName);
  }, [editFormData?.selectedLocations, locationList]);

  // Check for items with missing locations
  const itemsWithMissingLocations = React.useMemo(() => {
    if (!editingMenu?.categories || selectedMenuLocationNames.length === 0) return [];
    
    const itemsWithIssues = [];
    
    editingMenu.categories.forEach(category => {
      if (category.menuItemIds && category.menuItemIds.length > 0) {
        category.menuItemIds.forEach(menuItem => {
          const itemData = typeof menuItem === 'object' && menuItem !== null ? menuItem : null;
          if (itemData) {
            const itemLocations = getItemLocations(itemData);
            const missingLocations = selectedMenuLocationNames.filter(
              menuLoc => !itemLocations.includes(menuLoc)
            );
            
            if (missingLocations.length > 0) {
              itemsWithIssues.push({
                itemName: itemData.name || t('unnamedItem'),
                missingLocations: missingLocations,
              });
            }
          }
        });
      }
    });
    
    return itemsWithIssues;
  }, [editingMenu?.categories, selectedMenuLocationNames, t]);

  // Check for items with zero quantity (stockOnHand === 0)
  const itemsWithZeroQuantity = React.useMemo(() => {
    if (!editingMenu?.categories) return [];
    
    const itemsWithIssues = [];
    
    editingMenu.categories.forEach(category => {
      if (category.menuItemIds && category.menuItemIds.length > 0) {
        category.menuItemIds.forEach(menuItem => {
          const itemData = typeof menuItem === 'object' && menuItem !== null ? menuItem : null;
          if (itemData) {
            const variantZeroQty = []; // Array to store variant-specific zero quantity info
            const nonVariantZeroQtyLocations = []; // For non-variant items
            
            if (itemData.variantEnabled && itemData.variants && itemData.variants.length > 0) {
              // Check variants with perLocationStock
              itemData.variants.forEach(variant => {
                if (variant.perLocationStock && Array.isArray(variant.perLocationStock)) {
                  const variantZeroQtyLocations = [];
                  variant.perLocationStock.forEach(locationStock => {
                    if (locationStock.stockOnHand === 0 && locationStock.businessLocationName) {
                      const locationName = locationStock.businessLocationName;
                      if (!variantZeroQtyLocations.includes(locationName)) {
                        variantZeroQtyLocations.push(locationName);
                      }
                    }
                  });
                  
                  if (variantZeroQtyLocations.length > 0) {
                    variantZeroQty.push({
                      variantName: variant.name || t('unnamedVariant'),
                      locations: variantZeroQtyLocations,
                    });
                  }
                }
              });
            } else if (itemData.locationStock && Array.isArray(itemData.locationStock)) {
              // Check locationStock for non-variant items
              itemData.locationStock.forEach(locationStock => {
                if (locationStock.stockOnHand === 0 && locationStock.businessLocationName) {
                  const locationName = locationStock.businessLocationName;
                  if (!nonVariantZeroQtyLocations.includes(locationName)) {
                    nonVariantZeroQtyLocations.push(locationName);
                  }
                }
              });
            }
            
            if (variantZeroQty.length > 0 || nonVariantZeroQtyLocations.length > 0) {
              itemsWithIssues.push({
                itemName: itemData.name || t('unnamedItem'),
                hasVariants: itemData.variantEnabled && itemData.variants && itemData.variants.length > 0,
                variantZeroQty: variantZeroQty,
                nonVariantZeroQtyLocations: nonVariantZeroQtyLocations,
              });
            }
          }
        });
      }
    });
    
    return itemsWithIssues;
  }, [editingMenu?.categories, t]);

  if (!editingMenu) return null;

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          maxHeight: '90vh',
          width: { xs: '100vw', sm: '95vw', md: 700, lg: 800 },
          maxWidth: { xs: '100vw', sm: '95vw', md: 700, lg: 800 },
        }
      }}
    >
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Edit sx={{ color: 'primary.main' }} />
        {editingMenu ? (dialogTitle || t(tabValue === 0 ? 'editMenuSettings' : 'editCatalogSettings')) : ''}
      </DialogTitle>
      <DialogContent sx={{ position: 'relative' }}>
        {/* Loading Overlay */}
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
              borderRadius: 1,
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}
        
        <Box sx={{ pt: 1 }}>
          {/* Menu/Catalog Cover Picture */}
          <Typography sx={{ fontWeight: 600, color: "#222", fontSize: 15, mb: 0.7 }}>
            {tabValue === 0 ? t('menuCoverPicture') : t('catalogCoverPicture')}
          </Typography>
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Avatar
              variant="rounded"
              src={editingMenu.image ? editingMenu.image : undefined}
              alt={editingMenu.name}
              sx={{
                width: 200,
                height: 150,
                borderRadius: 2,
                bgcolor: "#f5f5f9",
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                mx: 'auto',
                mb: 2,
              }}
            >
              {!editingMenu.image && editingMenu.name ? editingMenu.name.charAt(0).toUpperCase() : "M"}
            </Avatar>
          </Box>

          {/* Menu/Catalog Name */}
          <Typography sx={{ fontWeight: 600, color: "#222", fontSize: 15, mb: 0.7 }}>
            {tabValue === 0 ? t('menuName') : t('catalogName')}
          </Typography>
          <TextField
            fullWidth
            value={editFormData.name || ''}
            onChange={(e) => setEditFormData({...editFormData, name: e.target.value, nameKey: e.target.value})}
            disabled={true}
            size="small"
            sx={{ 
              mb: 3, 
              maxWidth: 400,
              "& .MuiOutlinedInput-root": {
                bgcolor: "#E4E2E2",
                borderRadius: 2,
                color: "#979797",
              },
            }}
          />

          {/* Location Selection */}
          <Typography sx={{ fontWeight: 600, color: "#222", fontSize: 15, mb: 0.7 }}>
            {t('selectLocation')}
          </Typography>
          <Box sx={{ mb: 3 }}>
            <Autocomplete
              multiple
              options={locationList || []}
              getOptionLabel={(option) => option.businessLocationName || ""}
              value={editFormData.selectedLocations ? 
                (locationList || []).filter(loc => 
                  editFormData.selectedLocations.includes(loc._id)
                ) : []
              }
              onChange={(_, selectedOptions) => {
                const selectedIds = selectedOptions.map(option => option._id);
                setEditFormData({
                  ...editFormData,
                  selectedLocations: selectedIds
                });
              }}
              disabled={true}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  sx={{
                    mb: 0.5,
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#E4E2E2",
                      borderRadius: 2,
                      color: "#979797",
                    },
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option._id}
                    label={option.businessLocationName}
                    size="small"
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      '& .MuiChip-deleteIcon': {
                        display: 'none', // Hide delete icon since field is read-only
                      },
                    }}
                  />
                ))
              }
              clearOnBlur
              openOnFocus
              disableClearable
              isOptionEqualToValue={(option, value) =>
                option._id === value._id
              }
            />
            
            {/* Selected Locations Display */}
            {editFormData.selectedLocations && editFormData.selectedLocations.length > 0 && (
              <Box>
                <Typography sx={{ color: '#666', mb: 1, fontSize: '0.9em' }}>
                  {t('locationsSelectedCount', { count: editFormData.selectedLocations.length })}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {editFormData.selectedLocations.map((locationId) => {
                    const location = locationList?.find(loc => loc._id === locationId);
                    return location ? (
                      <Chip
                        key={locationId}
                        label={location.businessLocationName}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          '& .MuiChip-deleteIcon': {
                            display: 'none', // Hide delete icon since field is read-only
                          },
                        }}
                      />
                    ) : null;
                  })}
                </Box>
              </Box>
            )}
          </Box>

          {/* Menu/Catalog Settings */}
          <Typography sx={{ fontWeight: 600, color: "#222", fontSize: 15, mb: 0.7 }}>
            {tabValue === 0 ? t('menuSettings') : t('catalogSettings')}
          </Typography>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={true}
                    disabled={true}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Visibility sx={{ fontSize: 20, color: 'primary.main' }} />
                    <Typography>{t('showStockCountToCustomer')}</Typography>
                  </Box>
                }
                sx={{
                  p: 2,
                  border: '2px solid #f0f0f0',
                  borderRadius: 2,
                  bgcolor: '#f8f5ff',
                  borderColor: 'primary.main',
                  opacity: 0.7,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editFormData.publishMenu || false}
                    onChange={(e) => setEditFormData({...editFormData, publishMenu: e.target.checked})}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Public sx={{ fontSize: 20, color: 'primary.main' }} />
                    <Typography>{t('publishMenuLabel')}</Typography>
                  </Box>
                }
                sx={{
                  p: 2,
                  border: '2px solid #f0f0f0',
                  borderRadius: 2,
                  bgcolor: editFormData.publishMenu ? '#f8f5ff' : 'transparent',
                  borderColor: editFormData.publishMenu ? 'primary.main' : '#f0f0f0',
                }}
              />
            </Grid>
          </Grid>

          {/* Menu/Catalog Schedule */}
          <Typography sx={{ fontWeight: 600, color: "#222", fontSize: 15, mb: 0.7 }}>
            {tabValue === 0 ? t('menuSchedule') : t('catalogSchedule')}
          </Typography>
          <Box sx={{ mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<Schedule />}
              onClick={() => onScheduleClick?.(editingMenu)}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                borderRadius: 2,
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              }}
            >
              {t('schedule')}
            </Button>
          </Box>

          {/* Categories */}
          <Typography sx={{ fontWeight: 600, color: "#222", fontSize: 15, mb: 0.7 }}>
            {tabValue === 0 ? t('menuCategoriesWithCount', { count: editingMenu.categories?.length || 0 }) : t('catalogCategoriesWithCount', { count: editingMenu.categories?.length || 0 })}
          </Typography>
          {editingMenu.categories && editingMenu.categories.length > 0 ? (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ bgcolor: 'error.main', width: 32, height: 32 }}>
                  <Category sx={{ fontSize: 20, color: 'white' }} />
                </Avatar>
                <Typography sx={{ color: 'primary.main', fontWeight: 600 }}>
                  {t('categoriesCount', { count: editingMenu.categories.length })}
                </Typography>
              </Box>
              <List sx={{ bgcolor: '#fafafa', borderRadius: 2 }}>
                {editingMenu.categories.map((category, index) => (
                  <ListItem
                    key={category._id}
                    sx={{
                      borderBottom: index < editingMenu.categories.length - 1 ? '1px solid #e0e0e0' : 'none',
                      py: 2,
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
                      <ListItemAvatar>
                        <Avatar
                          variant="rounded"
                          src={category.image ? category.image : undefined}
                          alt={category.name}
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: 1,
                          }}
                        >
                          {!category.image && category.name ? category.name.charAt(0).toUpperCase() : "C"}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography sx={{ fontWeight: 600, color: '#333' }}>
                            {category.name}
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Category sx={{ fontSize: 16, color: 'error.main' }} />
                            <Typography sx={{ color: '#666', fontSize: 14 }}>
                              {t('itemsCount', { count: category.menuItemIds?.length || 0 })}
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                    {/* Menu Items List */}
                    {category.menuItemIds && category.menuItemIds.length > 0 && (
                      <Box sx={{ width: '100%', pl: 8, mt: 1 }}>
                        {category.menuItemIds.map((menuItem, itemIndex) => {
                          // Handle both cases: menuItem can be an object or just an ID
                          const itemData = typeof menuItem === 'object' && menuItem !== null ? menuItem : null;
                          const itemId = itemData?._id || menuItem;
                          const itemName = itemData?.name || t('unnamedItem');
                          const itemLocations = itemData ? getItemLocations(itemData) : [];
                          
                          return (
                            <Box
                              key={itemId || itemIndex}
                              sx={{
                                mb: itemIndex < category.menuItemIds.length - 1 ? 1.5 : 0,
                                pb: itemIndex < category.menuItemIds.length - 1 ? 1.5 : 0,
                                borderBottom: itemIndex < category.menuItemIds.length - 1 ? '1px solid #f0f0f0' : 'none',
                              }}
                            >
                              <Typography
                                onClick={() => itemData && onItemClick && onItemClick(itemData)}
                                sx={{
                                  fontWeight: 600,
                                  color: itemData ? 'primary.main' : '#666',
                                  fontSize: 14,
                                  cursor: itemData ? 'pointer' : 'default',
                                  textDecoration: itemData ? 'underline' : 'none',
                                  mb: 0.5,
                                  '&:hover': itemData ? {
                                    color: 'primary.dark',
                                  } : {},
                                }}
                              >
                                {itemName}
                              </Typography>
                              {itemLocations.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                  {itemLocations.map((locationName, locIndex) => (
                                    <Chip
                                      key={locIndex}
                                      icon={<LocationOn sx={{ fontSize: 14 }} />}
                                      label={locationName}
                                      size="small"
                                      sx={{
                                        bgcolor: '#f3e7ff',
                                        color: 'primary.main',
                                        fontSize: 12,
                                        height: 24,
                                        '& .MuiChip-icon': {
                                          color: 'primary.main',
                                        }
                                      }}
                                    />
                                  ))}
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : (
            <Typography sx={{ color: '#666', fontStyle: 'italic', mb: 3 }}>
              {t('noCategoriesAssigned')}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0, flexDirection: 'column', alignItems: 'stretch' }}>
        {/* Warnings Section - Only show when publish menu is enabled */}
        {editFormData?.publishMenu && (itemsWithMissingLocations.length > 0 || itemsWithZeroQuantity.length > 0) && (
          <Box sx={{ mb: 2, p: 2, bgcolor: '#fff3cd', borderRadius: 2, border: '1px solid #ffc107' }}>
            {itemsWithMissingLocations.length > 0 && (
              <Box sx={{ mb: itemsWithZeroQuantity.length > 0 ? 1.5 : 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Warning sx={{ color: '#ff9800', fontSize: 20, mt: 0.2 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: '#856404', fontSize: 14, mb: 0.5 }}>
                      {t('itemsNotAvailableInAllLocations')}
                    </Typography>
                    {itemsWithMissingLocations.map((item, index) => (
                      <Typography key={index} sx={{ color: '#856404', fontSize: 13, ml: 3, mb: 0.3 }}>
                        • {t('itemNotAvailableIn', { itemName: item.itemName, locations: item.missingLocations.join(', ') })}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Box>
            )}
            {itemsWithZeroQuantity.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Warning sx={{ color: '#ff9800', fontSize: 20, mt: 0.2 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: '#856404', fontSize: 14, mb: 0.5 }}>
                      {t('itemsWithZeroQuantityInLocations')}
                    </Typography>
                    {itemsWithZeroQuantity.map((item, index) => {
                      // For variant items, show variant-specific warnings
                      if (item.hasVariants && item.variantZeroQty.length > 0) {
                        return (
                          <Box key={index} sx={{ ml: 3, mb: 0.3 }}>
                            {item.variantZeroQty.map((variant, variantIndex) => (
                              <Typography key={variantIndex} sx={{ color: '#856404', fontSize: 13, mb: 0.2 }}>
                                • {t('itemVariantZeroQuantityIn', { itemName: item.itemName, variantName: variant.variantName, locations: variant.locations.join(', ') })}
                              </Typography>
                            ))}
                          </Box>
                        );
                      }
                      // For non-variant items
                      if (item.nonVariantZeroQtyLocations.length > 0) {
                        return (
                          <Typography key={index} sx={{ color: '#856404', fontSize: 13, ml: 3, mb: 0.3 }}>
                            • {t('itemZeroQuantityIn', { itemName: item.itemName, locations: item.nonVariantZeroQtyLocations.join(', ') })}
                          </Typography>
                        );
                      }
                      return null;
                    })}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            onClick={onClose}
            disabled={isLoading}
            sx={{ 
              color: '#fff',
              textTransform: 'lowercase',
              '&:disabled': {
                color: '#fff',
              },
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={onSave}
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              bgcolor: 'primary.main',
              textTransform: 'lowercase',
              '&:hover': { bgcolor: 'primary.dark' },
              '&:disabled': {
                bgcolor: '#ccc',
                color: '#666',
              },
            }}
          >
            {isLoading ? t('saving') : t('saveChanges')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EditMenuDialog;
